import React from 'react';
import { observer } from 'mobx-react';
import { EnsembleThreshold } from '../../../models/ensembling';
import { TreeNodeDatum, nodeIdOf } from './nodeDatum';
import { conditionTraits } from './conditionText';
import { TraitText } from './NodeTraits';
import {
  formatLift,
  separationLabel,
  separationTone,
  liftVsBaseline,
  separationTitle,
  splitSeparation,
} from './nodeLift';
import {
  nodeSegment,
  segmentColor,
  segmentTextColor,
  SEGMENT_LABEL,
} from './nodeSegment';
import './treeNodeCard.css';

// A tree node answers one of two questions, so it gets one of two shapes. An
// internal node asks something and shows the split it tests; a leaf states the
// outcome and shows the profile it describes. Previously both were a numbered
// circle, which meant the tree could be navigated but not read.
export { TreeNodeDatum, nodeIdOf };

const count = (n?: number) => (n ?? 0).toLocaleString('en-US');

/**
 * The node's own id, on every card. Every other surface refers to a node by
 * number (the diagnostics findings, the node panel heading, the agent's jump
 * intents), and the tree was the one place you could not read it off the screen.
 */
function NodeId({ datum }: { datum: TreeNodeDatum }) {
  const id = nodeIdOf(datum);
  if (id === null) return null;
  return <span className="tnc-id">#{id}</span>;
}

function Leaf({
  datum,
  rate,
  thresholds,
  baseline,
}: {
  datum: TreeNodeDatum;
  rate: number;
  thresholds?: EnsembleThreshold;
  baseline: number | null;
}) {
  const segment = nodeSegment(rate, thresholds);
  // The segment says itself twice: once as the badge pill, once as the card's
  // own edge. The pill names the band for someone reading a node, the edge
  // carries it at the zoom where the pill's text has stopped being legible.
  const colour = segmentColor(segment);
  const lift = liftVsBaseline(rate, baseline);
  // The path condition, not the split: a leaf is defined by everything true on
  // the way down to it, which is the profile someone would actually target.
  const traits = conditionTraits(datum.sumConditions);

  return (
    <div
      className={`tnc-leaf tnc-leaf--${
        segment ? segment.replace(' ', '-') : 'none'
      }`}
      style={colour ? { borderColor: colour } : undefined}
    >
      <div className="tnc-leaf-head">
        <NodeId datum={datum} />
        {segment && (
          <span
            className="tnc-badge"
            style={{
              background: colour ?? undefined,
              // White is unreadable on two of the four: measured against the
              // palette it is 2.1 on the Good teal and 1.7 on the Medium
              // yellow, and this is the smallest text on the card.
              color: segmentTextColor(segment) ?? undefined,
            }}
          >
            {SEGMENT_LABEL[segment] ?? segment}
          </span>
        )}
        {/* Coloured by direction, not by segment: lift says how this node
            compares to the average, which the node statistics panel also shows
            in blue or red. The segment is what the badge beside it is for. */}
        {lift !== null && (
          <span className={`tnc-lift ${lift < 1 ? 'tnc-lift--under' : ''}`}>
            {formatLift(lift)}
          </span>
        )}
      </div>
      <div className="tnc-traits">
        {traits.map((trait, i) => (
          <span className="tnc-trait" key={`${trait.text}-${i}`}>
            <TraitText trait={trait} />
          </span>
        ))}
      </div>
      <div className="tnc-foot">
        {count(datum.population)} leads &middot; {(rate * 100).toFixed(1)}%
      </div>
    </div>
  );
}

function Split({ datum }: { datum: TreeNodeDatum }) {
  const traits = conditionTraits(datum.splitConditions);
  // How far apart this cut pulls its two children, NOT this node's own lift. A
  // node's lift describes the leads that reached it, which the path above it
  // earned; a strong node can carry a cut that separates nothing at all, and
  // that is exactly the node someone scanning the tree wants to find.
  const separation = splitSeparation(datum.children);
  return (
    <div className="tnc-split">
      {/* First in the DOM and in a fixed-width column, so it sits at the same x on
          every split node and the tree can be scanned down one line instead of
          hunting for a badge that moved because a lead count got longer. Reading
          order matches: how well this cut separates is the question, and the rule
          that achieved it is the answer.

          In words, never a multiple. A leaf badge two inches away reads "2.2x" and
          means something else entirely, and one notation for two quantities is a
          reading somebody has to be taught. */}
      {separation && (
        <span
          className={`tnc-sep tnc-sep--${separationTone(separation)}`}
          title={separationTitle(separation)}
        >
          {separationLabel(separation)}
        </span>
      )}
      <span className="tnc-split-body">
        <span className="tnc-split-text">
          {traits.length ? (
            traits.map((trait, i) => (
              <React.Fragment key={`${trait.text}-${i}`}>
                {i > 0 && <span className="tpart tpart--word"> and </span>}
                <TraitText trait={trait} />
              </React.Fragment>
            ))
          ) : (
            <>Node {nodeIdOf(datum) ?? '?'}</>
          )}
        </span>
        <span className="tnc-count">
          <NodeId datum={datum} />
          {count(datum.population)} leads
        </span>
      </span>
    </div>
  );
}

function TreeNodeCard({
  nodeId,
  datum,
  baseline,
  thresholds,
}: {
  nodeId: number;
  datum: TreeNodeDatum;
  baseline: number | null;
  thresholds?: EnsembleThreshold;
}) {
  const isSelected = nodeIdOf(datum) === nodeId;
  const isLeaf = !datum.children?.length;
  const rate = datum.population
    ? Number(datum.conversions) / Number(datum.population)
    : 0;

  return (
    <div className={`tnc ${isSelected ? 'tnc--selected' : ''}`}>
      {isLeaf ? (
        <Leaf
          datum={datum}
          rate={rate}
          thresholds={thresholds}
          baseline={baseline}
        />
      ) : (
        <Split datum={datum} />
      )}
    </div>
  );
}

// Observed so nodes recolour once the ensembling configuration arrives.
export default observer(TreeNodeCard);
